import React, { useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import 'xterm/css/xterm.css';
import useTerminalWebSocket from '../../hooks/useTerminalWebSocket';

const THEME = {
  background: '#0a0a0a',
  foreground: '#e0e0e0',
  cursor: '#00ff88',
  cursorAccent: '#0a0a0a',
  selectionBackground: '#00ff8833',
  black: '#0a0a0a',
  red: '#ff5555',
  green: '#00ff88',
  yellow: '#f1fa8c',
  blue: '#6272a4',
  magenta: '#ff79c6',
  cyan: '#8be9fd',
  white: '#e0e0e0',
  brightBlack: '#6272a4',
  brightRed: '#ff6e6e',
  brightGreen: '#69ff94',
  brightYellow: '#ffffa5',
  brightBlue: '#d6acff',
  brightMagenta: '#ff92df',
  brightCyan: '#a4ffff',
  brightWhite: '#ffffff',
};

const STATUS_MESSAGES = {
  connecting: '\x1b[1;33m[SYLink Terminal]\x1b[0m Connecting...\r\n',
  connected: '\x1b[1;32m[SYLink Terminal]\x1b[0m Connected.\r\n',
  disconnected: '\x1b[1;31m[SYLink Terminal]\x1b[0m Session closed.\r\n',
  reconnecting: '\x1b[1;33m[SYLink Terminal]\x1b[0m Reconnecting...\r\n',
  error: (msg) => `\x1b[1;31m[SYLink Terminal]\x1b[0m Error: ${msg}\r\n`,
};

/**
 * Interactive xterm.js terminal connected to Metasploit session via WebSocket.
 *
 * Props:
 * - sessionId: exploit session UUID
 * - onConnectionChange(state): callback when connection state changes
 * - disabled: if true, don't connect
 * - className: extra CSS classes on the container
 *
 * Ref exposes:
 * - sendCommand(cmd): programmatically send a command (for quick commands)
 */
const XtermTerminal = forwardRef(({
  sessionId,
  onConnectionChange,
  onChainStep,
  onChainComplete,
  onEDRDetected,
  onPivotMap,
  onMigrationSuggestion,
  disabled = false,
  className = '',
}, ref) => {
  const containerRef = useRef(null);
  const termRef = useRef(null);
  const fitAddonRef = useRef(null);
  const prevStateRef = useRef(null);

  // Output callback writes to xterm
  const handleOutput = useCallback((data) => {
    if (termRef.current) {
      termRef.current.write(data);
    }
  }, []);

  // Connected callback writes welcome banner
  const handleConnected = useCallback((info) => {
    if (termRef.current) {
      termRef.current.write(STATUS_MESSAGES.connected);
      const target = info?.target_os || info?.session_type || 'shell';
      const user = info?.username || '';
      termRef.current.write(
        `\x1b[1;32m[SYLink Terminal]\x1b[0m Session: ${info?.msf_session_id || '?'} | Type: ${target}${user ? ` | User: ${user}` : ''}\r\n\r\n`
      );
    }
  }, []);

  const {
    connectionState,
    isConnected,
    error,
    sendInput,
    sendResize,
    sendChain,
    sendDetectEDR,
    sendPivotScan,
    sendSuggestMigrate,
  } = useTerminalWebSocket(sessionId, {
    onOutput: handleOutput,
    onConnected: handleConnected,
    onChainStep,
    onChainComplete,
    onEDRDetected,
    onPivotMap,
    onMigrationSuggestion,
    enabled: !disabled,
  });

  // Notify parent of state changes
  useEffect(() => {
    if (prevStateRef.current !== connectionState) {
      prevStateRef.current = connectionState;
      if (onConnectionChange) onConnectionChange(connectionState);
    }
  }, [connectionState, onConnectionChange]);

  // Write status messages to terminal
  useEffect(() => {
    if (!termRef.current) return;
    if (connectionState === 'connecting') {
      termRef.current.write(STATUS_MESSAGES.connecting);
    } else if (connectionState === 'error' && error) {
      termRef.current.write(STATUS_MESSAGES.error(error));
    } else if (connectionState === 'disconnected' && prevStateRef.current) {
      termRef.current.write(STATUS_MESSAGES.disconnected);
    }
    // eslint-disable-next-line
  }, [connectionState, error]);

  // Initialize xterm.js
  useEffect(() => {
    if (!containerRef.current) return;

    const term = new Terminal({
      cursorBlink: true,
      cursorStyle: 'block',
      fontSize: 14,
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'Menlo', monospace",
      theme: THEME,
      scrollback: 5000,
      convertEol: true,
      allowProposedApi: true,
    });

    const fitAddon = new FitAddon();
    const webLinksAddon = new WebLinksAddon();

    term.loadAddon(fitAddon);
    term.loadAddon(webLinksAddon);
    term.open(containerRef.current);

    // Initial fit
    try { fitAddon.fit(); } catch (_) { /* noop */ }

    termRef.current = term;
    fitAddonRef.current = fitAddon;

    // Handle user keyboard input — raw mode, no local echo
    const onDataDisposable = term.onData((data) => {
      sendInput(data);
    });

    // Window resize handler
    const handleResize = () => {
      try {
        fitAddon.fit();
        sendResize(term.cols, term.rows);
      } catch (_) { /* noop */ }
    };

    window.addEventListener('resize', handleResize);

    // ResizeObserver on container for layout changes
    let resizeObserver;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        try {
          fitAddon.fit();
          sendResize(term.cols, term.rows);
        } catch (_) { /* noop */ }
      });
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      onDataDisposable.dispose();
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) resizeObserver.disconnect();
      term.dispose();
      termRef.current = null;
      fitAddonRef.current = null;
    };
    // eslint-disable-next-line
  }, []);

  // Expose imperative methods for parent (quick commands + Phase 3)
  useImperativeHandle(ref, () => ({
    sendCommand: (cmd) => {
      if (termRef.current && isConnected) {
        sendInput(cmd + '\n');
      }
    },
    sendChain: (chainId) => { sendChain(chainId); },
    sendDetectEDR: () => { sendDetectEDR(); },
    sendPivotScan: () => { sendPivotScan(); },
    sendSuggestMigrate: () => { sendSuggestMigrate(); },
    focus: () => {
      if (termRef.current) termRef.current.focus();
    },
    clear: () => {
      if (termRef.current) termRef.current.clear();
    },
  }), [isConnected, sendInput, sendChain, sendDetectEDR, sendPivotScan, sendSuggestMigrate]);

  return (
    <div
      ref={containerRef}
      className={`w-full h-full ${className}`}
      style={{ minHeight: 0 }}
    />
  );
});

XtermTerminal.displayName = 'XtermTerminal';

export default XtermTerminal;
